import React, { useState, useEffect, useContext, ChangeEvent } from 'react'
import ReactDOM from 'react-dom'
import { Emit_main } from "../navigation"
import * as P from "../program-state"
import * as C from "../controls"
import { faCalendar, faCircleChevronLeft, faCircleChevronRight, faFloppyDisk, faPlus, faUpload } from '@fortawesome/free-solid-svg-icons'
import { General_preview_component } from "./campaigns/common"
import { Enhanced_campaign_preview, Static_preview_component } from "../components/campaign_preview"
import {useForm, Controller, SubmitHandler} from "react-hook-form"
import { useMultistepCreateCampaignContext, MultistepCreateCampaignProvider, Multistep_campaign_data } from "./campaigns/campaign_create_provider"
import { format, formatISO, set } from "date-fns"
import { useNavigate } from 'react-router-dom'
import {z} from "zod"
import { fetch_available_ctas } from "./campaigns/cta_helpers"

import { cn } from "../lib/utils"
import { Button } from "./components/button"
import { Calendar } from "./components/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./components/popover"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MqttInitializer, {MqttContext} from "./mqtt/mqtt_initializer"
import { Placement_selection_with_preview, placement_meta } from "./campaigns/common"
import { linkedin_bid_suggestion_t } from "../resource/types"
import {linkedin_bid_suggestion_payload_t, get_linkedin_bid_suggestion, preview_data_t } from "./campaigns/common"
import {Audience_widget_horizontal} from "./audiences-individual-editor"


type objective_option = {
    key : string;
    display : string;
}

export // TikTok sandbox advertiser, used until a real advertiser id is wired through
// from the brand. It was pasted inline in three places.
const TIKTOK_SANDBOX_ADVERTISER_ID = import.meta.env.VITE_TIKTOK_ADVERTISER_ID ?? '7195179483962621953'

const objectives_dropdown : objective_option[] = [
    { key: 'LINK_CLICKS', display: 'Link clicks' },
    { key: 'BRAND_AWARENESS', display: 'Brand awareness' },
    { key: 'CONVERSIONS', display: 'Conversions' },
    { key: 'ENGAGEMENT', display: 'Engagement' },
    { key: 'LEAD_GENERATION', display: 'Lead generation' },
    { key: 'APP_INSTALLS', display: 'App installs' }
]


type Campaign_settings_step_1_form = {
    name: string;
    objective: string;
    start_date: string;
    networks: string[];
    brand_id: number;
    audience_id: number;
    start_time: string;
    budget_mode: string;
    meta_budget: number;
    meta_bid_amount: number;
    facebook_pixel_id: string;
    // meta options that used to be fixed in code
    meta_pixel_id: string;
    meta_budget_level: string;
    meta_bid_strategy: string;
    meta_advantage_audience: boolean;
    meta_leadgen_form_id: string;
    linkedin_budget: number;
    linkedin_bid_amount: number;
    linkedin_placements: string[];
    linkedin_conversion_tracking_id: number;
    tiktok_budget: number;
    tiktok_bid_amount: number;
    tiktok_pixel_id: number;
    tiktok_placements: string[];
    x_budget: number;
    x_bid_amount: number;
    x_placements: string[];
}


type Creative_detail_step_2_form = {
    headline: string;
    message: string;
    link: string;
    media: any;
}


interface smart_campaign_payload_t {
    name : string;
    objective : string;
    brand_id : number;
    networks : string[];
    budget_mode : string;
    start_time : string;
    end_time : string;
    audience_id : number;
}

interface smart_creative_payload_t {
    name : string;
    headline : string;
    message : string;
    link : string;
    file_id : number;
}

interface facebook_specific_config_t {
    budget : number;
    bid_amount : number;
    pixel_id? : string;
    placements? : string[];
}

interface linkedin_specific_config_t {
    budget : number;
    bid_amount : number;
    conversion_tracking_id? : number;
    placements? : string[];
}

interface tiktok_specific_config_t {
    budget : number;
    bid_amount : number;
    placements? : string[];
}


interface campaign_creation_t {
    smart_campaign : smart_campaign_payload_t;
    smart_creative : smart_creative_payload_t;
    specific_config__facebook? : facebook_specific_config_t;
    specific_config__linkedin? : linkedin_specific_config_t;
    specific_config__tiktok? : tiktok_specific_config_t;
}


const linkedin_placement_with_images : placement_meta[] = [
    {
        "key": "FEED",
        "display": "Linkedin Feed"
    },
    {
        "key": "dynamic__follower_ads",
        "display": "Linkedin Follower Ads"
    },
    {
        "key": "dynamic__job_ads",
        "display": "Linkedin Job Ads"
    },
    {
        "key": "dynamic__spotlight_ads",
        "display": "Linkedin Spotlight Ads"
    },
    {
        "key": "text_ad",
        "display": "Linkedin Right Column"
    },
    // temporary disable the selection of text and message ads
    // {
    //     "key": "dynamic__message_ads",
    //     "display": "Linkedin Message Ads",
    //     "image_url": "https://impactable.com/wp-content/uploads/2023/09/image-7.png"
    // }
]

const facebook_placement_with_images : placement_meta[] = [
    {
        "key": "facebook_positions__feed",
        "display": "Facebook Feed",
    },
    {
        "key": "facebook_positions__right_hand_column",
        "display": "Facebook Right Column",
    },
    {
        "key": "facebook_positions__marketplace",
        "display": "Facebook Marketplace",
    },
    {
        "key": "facebook_positions__story",
        "display": "Facebook Story",
    },
    {
        "key": "facebook_positions__search",
        "display": "Facebook Search",
    },
    {
        "key": "facebook_positions__instream_video",
        "display": "Facebook instream video",
    },
    {
        "key": "facebook_positions__facebook_reels",
        "display": "Facebook reels",
    },
    {
        "key": "facebook_positions__profile_feed",
        "display": "Facebook profile feed",
    },
    {
        "key": "instagram_positions__stream",
        "display": "Instagram stream",
    },
    {
        "key": "instagram_positions__story",
        "display": "Instagram story",
    },
    {
        "key": "instagram_positions__explore",
        "display": "Instagram explore",
    },
    {
        "key": "instagram_positions__explore_home",
        "display": "Instagram explore home",
    },
    {
        "key": "instagram_positions__reels",
        "display": "Instagram reels",
    },
    {
        "key": "instagram_positions__profile_feed",
        "display": "Instagram profile feed",
    },
    {
        "key": "instagram_positions__ig_search",
        "display": "Instagram search",
    },
    {
        "key": "instagram_positions__profile_reels",
        "display": "Instagram profile reels",
    },
    {
        "key": "messenger_positions__messenger_home",
        "display": "Messenger inbox",
    },
    {
        "key": "messenger_positions__sponsored_messages",
        "display": "Messenger sponsored messages",
    },
    {
        "key": "messenger_positions__story",
        "display": "Messenger story",
    }
]


const tiktok_placement_with_images : placement_meta[] = [
    {
        "key": "TIKTOK_MAIN_FEED",
        "display": "TikTok Main Feed"
    },
    {
        "key": "TIKTOK_IN_FEED",
        "display": "In-Feed Ads"
    },
    {
        "key": "TIKTOK_TOP_VIEW",
        "display": "Top View Ads"
    },
    {
        "key": "TIKTOK_PANGLE",
        "display": "Pangle Network"
    }
]

const x_placement_with_images : placement_meta[] = [
    {
        "key": "publisher_network",
        "display": "X Feed (Timeline)"
    },
    {
        "key": "x_profile",
        "display": "X Profile"
    },
    {
        "key": "x_search_results",
        "display": "X Search Results"
    },
    {
        "key": "x_audience_network",
        "display": "X Audience Network"
    }
]


const Date_picker = ({selected, on_select} : {selected: Date | undefined, on_select: (day: Date | undefined) => void}) => {
    return (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "tw-w-full tw-justify-start tw-justify-left tw-text-left tw-font-normal",
                !selected && "tw-text-muted-foreground"
              )}
            >
              <FontAwesomeIcon icon={faCalendar} className="tw-mr-2 tw-h-4 tw-w-4" />
              {selected ? format(selected, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="tw-w-auto tw-p-0" align="start">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={on_select}
              initialFocus
            />
          </PopoverContent>
        </Popover>
    )
}


const Step1_form = () => {
    const [start_date, set_start_date] = React.useState<Date | undefined>(new Date())
    const [end_date, set_end_date] = React.useState<Date | undefined>(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))
    const [state, dispatch] = useMultistepCreateCampaignContext()
    const [saved_audiences, ps, us] = P.use_saved_audiences()
    const [brands] = P.use_brands()
    const [, pixel_cs] = P.use_authenticated_program_state()
    const [meta_pixels, set_meta_pixels] = useState<P.facebook_pixel_parse_t[]>([])

    // The pixels this brand can choose from. The pixel used to come only from
    // the brand, so a campaign could not override it, and nothing told the user
    // when a conversion objective had no pixel at all - the api accepted the
    // campaign and the worker failed later, out of sight.
    useEffect(() => {
        let cancelled = false
        const load_pixels = async () => {
            const facebook_network = pixel_cs.connected_networks?.find(
                (network: any) => network.network_tag === 'nw-facebook')
            if (!facebook_network) return
            const [ok, pixels] = await P.issue_json_get(
                P.v1api`social/facebook/${facebook_network.eres_dbid}/pixels`,
                P.facebook_pixels_parse,
                pixel_cs.token)
            if (ok && !cancelled) set_meta_pixels(pixels)
        }
        void load_pixels()
        return () => { cancelled = true }
    }, [pixel_cs.connected_networks, pixel_cs.token])

    // exclude 'networks' and 'available_ctas' from form defaultValues (managed separately)
    const { networks: _, available_ctas: __, ...step1_without_networks } = state?.data?.step1 || {}
    const { register, handleSubmit, watch, control, formState: { errors }, setError, clearErrors } = useForm<Campaign_settings_step_1_form>(
        {defaultValues: step1_without_networks as any}
    )
    const [target_networks, set_target_networks] = useState<{
        [key: string]: boolean;
    }>(state?.data?.step1?.networks || {
        meta: false,
        linkedin: false,
        tiktok: false,
        x: false
    });

    const brands_data = !!brands ? brands.map((b) => {
        return {key: String(b.id), display: b.name}
    }) : []

    const audiences_data = !!saved_audiences ? saved_audiences.map((a) => {
        return {key: String(a.id), display: a.name}
    }) : []

    // get the selected brand details
    const selected_brand = brands?.find(b => String(b.id) === String(state?.data?.step1?.brand_id));

    // determine which networks are available for the selected brand
    const available_networks = React.useMemo(() => {
        if (!selected_brand) {
            return { meta: false, linkedin: false, tiktok: false, x: false }
        }

        return {
            meta: !!(selected_brand.meta_page && selected_brand.ad_account_id),
            linkedin: !!(selected_brand.linkedin_org && selected_brand.linkedin_ad_account),
            tiktok: !!(selected_brand.tiktok_user_id && selected_brand.tiktok_identity_id && selected_brand.tiktok_ad_account_id),
            x: !!(selected_brand.x_user_id && selected_brand.x_ad_account_id)
        }
    }, [selected_brand?.id, selected_brand?.meta_page, selected_brand?.ad_account_id, selected_brand?.linkedin_org, selected_brand?.linkedin_ad_account, selected_brand?.tiktok_user_id, selected_brand?.tiktok_identity_id, selected_brand?.tiktok_ad_account_id, selected_brand?.x_user_id, selected_brand?.x_ad_account_id])

    // uncheck unavailable networks when brand changes
    React.useEffect(() => {
        if (!available_networks.meta && target_networks.meta) {
            handle_checkbox_change({ target: { name: 'meta', checked: false } } as any)
        }
        if (!available_networks.linkedin && target_networks.linkedin) {
            handle_checkbox_change({ target: { name: 'linkedin', checked: false } } as any)
        }
        if (!available_networks.tiktok && target_networks.tiktok) {
            handle_checkbox_change({ target: { name: 'tiktok', checked: false } } as any)
        }
        if (!available_networks.x && target_networks.x) {
            handle_checkbox_change({ target: { name: 'x', checked: false } } as any)
        }
    }, [available_networks.meta, available_networks.linkedin, available_networks.tiktok, available_networks.x, target_networks.meta, target_networks.linkedin, target_networks.tiktok, target_networks.x])

    // this help set the default brand id and audience id when user first load the form
    useEffect(() => {
        if (brands_data.length > 0 && !state?.data.step1?.brand_id) {
            dispatch({type: 'SET_STEP1_DATA', payload: {brand_id: brands_data[0].key}})
        }

        if (audiences_data.length > 0 && !state?.data.step1?.audience_id) {
            dispatch({type: 'SET_STEP1_DATA', payload: {audience_id: audiences_data[0].key}})
        }
    }, [brands_data, audiences_data])

    // fetch available ctas when brand, objective, or networks change
    useEffect(() => {
        const fetch_ctas = async () => {
            if (!state?.data?.step1?.brand_id || !state?.data?.step1?.objective) {
                return
            }

            const selected_network_keys = Object.keys(target_networks).filter(
                (key) => target_networks[key] === true
            )

            if (selected_network_keys.length === 0) {
                return
            }

            // map "meta" to "facebook" for backend api
            const network_names_for_api = selected_network_keys.map((key) =>
                key === "meta" ? "facebook" : key
            )

            const request_payload = {
                brand_id: Number(state.data.step1.brand_id),
                objective: state.data.step1.objective,
                networks: network_names_for_api
            }

            const available_ctas = await fetch_available_ctas(
                request_payload,
                us.token as P.authn_token
            )

            if (available_ctas) {
                dispatch({
                    type: 'SET_AVAILABLE_CTAS',
                    payload: { available_ctas }
                })

                // reset cta selections if they're no longer valid
                const current_meta_cta = state.data.step1.meta_cta
                const current_linkedin_cta = state.data.step1.linkedin_cta
                const current_tiktok_cta = state.data.step1.tiktok_cta

                const meta_cta_still_valid = available_ctas.facebook.some(
                    (cta) => cta.value === current_meta_cta
                )
                const linkedin_cta_still_valid = available_ctas.linkedin.some(
                    (cta) => cta.value === current_linkedin_cta
                )
                const tiktok_cta_still_valid = available_ctas.tiktok.some(
                    (cta) => cta.value === current_tiktok_cta
                )

                if (!meta_cta_still_valid && current_meta_cta) {
                    dispatch({type: 'SET_STEP1_DATA', payload: {meta_cta: undefined}})
                }
                if (!linkedin_cta_still_valid && current_linkedin_cta) {
                    dispatch({type: 'SET_STEP1_DATA', payload: {linkedin_cta: undefined}})
                }
                if (!tiktok_cta_still_valid && current_tiktok_cta) {
                    dispatch({type: 'SET_STEP1_DATA', payload: {tiktok_cta: undefined}})
                }
            }
        }

        fetch_ctas()
    }, [state?.data?.step1?.brand_id, state?.data?.step1?.objective, target_networks])

    const [linkedin_min_bid, set_linkedin_min_bid] = useState<{[key: string] : string | null}>({
        "FEED": null,
        "text_ad": null,
        "dynamic__follower_ads": null,
        "dynamic__job_ads": null,
        "dynamic__spotlight_ads": null,
        "dynamic__message_ads": null
    })

    const find_largest_bid = (selected : string[]) => {
        let max_bid = -Infinity;
        for (let key of selected) {
            let value = linkedin_min_bid[key]
            if (value !== null) {
                let number_value = parseFloat(value)
                if (number_value > max_bid) {
                    max_bid = number_value
                }
            }
        }
        return max_bid
    }

    const handle_checkbox_change = async (event : React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        const new_values = {
            ...target_networks,
            [name]: checked,
        }
        set_target_networks(new_values);
        if (name === "linkedin") {
            await scout_linkedin_minbid_on_placments()
            state && validate_linkedin_bid(state.data.step1.linkedin_placements, state.data.step1.linkedin_bid_amount)
        }
        dispatch({ type: 'SET_TARGET_NETWORKS', payload: { networks: new_values }})
    };

    const on_start_date_selected = (day: Date | undefined) => {
        if (day === undefined) {
            return
        }
        set_start_date(day)
        dispatch({ type: 'SET_START_END_DATE', payload: { start_time: formatISO(day, { representation: 'complete' }) }})
    }

    const on_end_date_selected = (day: Date | undefined) => {
        if (day === undefined) {
            return
        }
        set_end_date(day)
        dispatch({ type: 'SET_START_END_DATE', payload: { end_time: formatISO(day, { representation: 'complete' }) }})
    }

    const onSubmit: SubmitHandler<Campaign_settings_step_1_form> = async (data) => {
        // validation and submission happen in submit_the_form; this exists only
        // because the form element needs an onSubmit
    }

    const scout_linkedin_minbid_on_placments = async () => {
        let available_placements : string[] = linkedin_placement_with_images.map((item) => item.key)

        if (state) {
            let new_bid_map : Record<string, string> = {}
            for (const placement of available_placements) {
                let campaign_type : string = ""
                let objective : string = "WEBSITE_VISIT"

                switch (placement) {
                    case "FEED":
                        campaign_type = "SPONSORED_UPDATES"
                        objective = state.data.step1.objective
                        break
                    case "text_ad":
                        campaign_type = "TEXT_AD"
                        break
                    case "dynamic__follower_ads":
                        campaign_type = "DYNAMIC"
                        break
                    case "dynamic__job_ads":
                        campaign_type = "DYNAMIC"
                        break
                    case "dynamic__spotlight_ads":
                        campaign_type = "DYNAMIC"
                        break
                    case "dynamic__message_ads":
                        campaign_type = "DYNAMIC"
                        break
                }
                
                let payload : linkedin_bid_suggestion_payload_t = {
                    objective: objective,
                    brand_id: state.data.step1.brand_id,
                    audience_id: state.data.step1.audience_id,
                    budget: state.data.step1.linkedin_budget,
                    campaign_type: campaign_type
                }
                const bid_suggestion : linkedin_bid_suggestion_t | null = await get_linkedin_bid_suggestion(us.token, payload)
                console.log(placement, ", ", "Bid suggestion: ", bid_suggestion)
                if (bid_suggestion) {
                    new_bid_map[placement] = bid_suggestion.suggestedBid?.min?.amount
                }
            }
            set_linkedin_min_bid(new_bid_map)
        }
        state && validate_linkedin_bid(state.data.step1.linkedin_placements, state.data.step1.linkedin_bid_amount)
    }

    const handle_linkedin_placements_change = async (selected : string[]) => {
        dispatch({ type: 'SET_STEP1_DATA', payload: {linkedin_placements: selected} })
        state && validate_linkedin_bid(selected, state.data.step1.linkedin_bid_amount)
    }

    const handle_facebook_placements_change = (selected : string[]) => {
        dispatch({ type: 'SET_STEP1_DATA', payload: {meta_placements: selected} })
    }

    const handle_tiktok_placements_change = (selected : string[]) => {
        dispatch({ type: 'SET_STEP1_DATA', payload: {tiktok_placements: selected} })
    }

    const handle_x_placements_change = (selected : string[]) => {
        dispatch({ type: 'SET_STEP1_DATA', payload: {x_placements: selected} })
    }

    const validate_linkedin_bid = (placements : string[], input_bid_amount : number | undefined) => {
        let suggested_bid = find_largest_bid(placements)
        console.log(".i. linkedin bid_amount: ", input_bid_amount, ", suggested_bid: ", suggested_bid);
        if (input_bid_amount && (input_bid_amount < suggested_bid)) {
            setError("linkedin_bid_amount", {message: "Bid amount is lower than the suggested bid amount. Minimum bid amount is " + suggested_bid})
        } else {
            clearErrors("linkedin_bid_amount")
        }
    }

    watch((data, { name, type }) => {
        // exclude networks and available_ctas from payload (managed separately)
        const { networks, available_ctas, ...data_without_managed_fields } = data as any

        // filter out undefined values
        const filtered_data = Object.fromEntries(
            Object.entries(data_without_managed_fields).filter(([_, v]) => v !== undefined)
        )

        dispatch({ type: 'SET_STEP1_DATA', payload: filtered_data })

        if (name === "linkedin_bid_amount") {
            let placements = data["linkedin_placements"];
            if (placements && placements.length > 0) {
                validate_linkedin_bid(placements as string[], data["linkedin_bid_amount"])
            }
        }
    })

    const budget_mode_watcher = watch('budget_mode');

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-start sm:tw-items-center">
            <C.Eres_form_label htmlFor="eres-campaign-name" required>Campaign name</C.Eres_form_label>
            <div className='tw-w-full tw-flex tw-flex-col'>
                <C.Eres_line_edit id="eres-campaign-name" aria-label="campaign name" defaultValue="" type="text" extra={register("name", { required: true })} />
                { errors.name?.type === "required" && <C.Eres_form_error className="tw-mt-0" msg="Please enter a non-empty name."/> }
            </div>
            <C.Eres_form_label htmlFor="eres-objective" required>Objective</C.Eres_form_label>
            <div>
            <select id="objective" className={`tw-w-full tw-transition tw-h-9 tw-m-0 tw-text-black tw-font-normal tw-text-base ${C.form_element_styles}`} {...register("objective")} >
                {
                    objectives_dropdown.map((item) => (
                        <option key={item.key} value={item.key}>{item.display}</option>
                    ))
                }
            </select>
            </div>
            <C.Eres_form_label htmlFor="eres-brand" required>Brand</C.Eres_form_label>
            <Controller name="brand_id" control={control} render={({field}) => (
                <C.Eres_combobox
                    id="e_brand_id"
                    data={brands_data}
                    defaultValue={brands_data.length ? brands_data[0].key : undefined}
                    search_placeholder="Search for a brand ..."
                    empty_selection_text="No brand selected"
                    unselect_placeholder="Don't use a brand"
                    no_matches_placeholder="No matching brands"
                    {...field}
                />
            )}/>
            <C.Eres_form_label htmlFor="eres-audience" required>Audience</C.Eres_form_label>
            <div>
                <Controller name="audience_id" control={control} render={({field}) => (
                    <C.Eres_combobox
                        id="e_audience_id"
                        data={audiences_data}
                        defaultValue={audiences_data.length ? audiences_data[0].key : undefined}
                        search_placeholder="Search for an audience ..."
                        empty_selection_text="No audience selected"
                        unselect_placeholder="Don't use an audience"
                        no_matches_placeholder="No matching audiences"
                        {...field}
                    />
                )}/>
                <C.Eres_collapse title="View audience detail">
                    <Audience_widget_horizontal audience_id={state?.data.step1.audience_id} />
                </C.Eres_collapse>
            </div>
            <C.Eres_form_label htmlFor="eres-location" required>Target networks</C.Eres_form_label>
            <div className='tw-flex tw-flex-col tw-space-y-4'>
                <div className="tw-flex tw-items-center">
                    <input
                        onChange={handle_checkbox_change}
                        checked={target_networks.meta}
                        disabled={!available_networks.meta}
                        id="checkbox-1"
                        name="meta"
                        type="checkbox"
                        value="meta"
                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded tw-focus:ring-blue-500 tw-focus:ring-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    />
                    <label
                        htmlFor="checkbox-1"
                        className={`tw-ms-2 tw-text-sm tw-font-medium ${available_networks.meta ? 'tw-cursor-pointer tw-text-gray-900' : 'tw-text-gray-400 tw-cursor-not-allowed'}`}
                    >
                        Meta {!available_networks.meta && '(no account connected)'}
                    </label>
                </div>

                <div className='tw-flex tw-items-center tw-space-x-4'>
                    <div className="tw-flex tw-items-center">
                        <input
                            onChange={handle_checkbox_change}
                            checked={target_networks.linkedin}
                            disabled={!available_networks.linkedin}
                            id="checkbox-3"
                            name="linkedin"
                            type="checkbox"
                            value="linkedin"
                            className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded tw-focus:ring-blue-500 tw-focus:ring-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        />
                        <label
                            htmlFor="checkbox-3"
                            className={`tw-ms-2 tw-text-sm tw-font-medium ${available_networks.linkedin ? 'tw-cursor-pointer tw-text-gray-900' : 'tw-text-gray-400 tw-cursor-not-allowed'}`}
                        >
                            Linkedin {!available_networks.linkedin && '(no account connected)'}
                        </label>
                    </div>
                </div>

                <div className='tw-flex tw-items-center tw-space-x-4'>
                    <div className="tw-flex tw-items-center">
                        <input
                            onChange={handle_checkbox_change}
                            checked={target_networks.tiktok}
                            disabled={!available_networks.tiktok}
                            id="checkbox-4"
                            name="tiktok"
                            type="checkbox"
                            value="tiktok"
                            className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded tw-focus:ring-blue-500 tw-focus:ring-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        />
                        <label
                            htmlFor="checkbox-4"
                            className={`tw-ms-2 tw-text-sm tw-font-medium ${available_networks.tiktok ? 'tw-cursor-pointer tw-text-gray-900' : 'tw-text-gray-400 tw-cursor-not-allowed'}`}
                        >
                            Tiktok {!available_networks.tiktok && '(no account connected)'}
                        </label>
                    </div>
                </div>

                <div className='tw-flex tw-items-center tw-space-x-4'>
                    <div className="tw-flex tw-items-center">
                        <input
                            onChange={handle_checkbox_change}
                            checked={false}
                            disabled={true}
                            id="checkbox-5"
                            name="x"
                            type="checkbox"
                            value="x"
                            className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded tw-focus:ring-blue-500 tw-focus:ring-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        />
                        <label
                            htmlFor="checkbox-5"
                            className="tw-ms-2 tw-text-sm tw-font-medium tw-text-gray-400 tw-cursor-not-allowed"
                        >
                            X (not yet supported)
                        </label>
                    </div>
                </div>
            </div>
            <C.Eres_form_label htmlFor="budget-mode">Budget mode</C.Eres_form_label>
            <div className='tw-flex tw-space-x-3'>
                <div className="tw-flex tw-items-center">
                    <input id="budget-option-daily" type="radio" value="DAILY" className="tw-cursor-pointer tw-w-4 tw-h-4" {...register('budget_mode')} />
                    <label htmlFor="budget-option-daily" className="tw-block tw-ms-2">
                        Daily budget
                    </label>
                </div>
                <div className="tw-flex tw-items-center">
                    <input id="budget-option-lifetime" type="radio" value="LIFETIME" className="tw-cursor-pointer tw-w-4 tw-h-4" {...register('budget_mode')} />
                    <label htmlFor="budget-option-lifetime" className="tw-block tw-ms-2">
                        Lifetime budget (end date is required)
                    </label>
                </div>
            </div>
            <C.Eres_form_label htmlFor="eres-start-date" required>Start date</C.Eres_form_label>
            <Date_picker selected={start_date} on_select={on_start_date_selected} />
            {
                budget_mode_watcher === "LIFETIME" &&
                <>
                    <C.Eres_form_label htmlFor="eres-end-date" required>End date</C.Eres_form_label>
                    <Date_picker selected={end_date} on_select={on_end_date_selected} />
                </>
            }

            {/* cta section */}
            { (target_networks.meta || target_networks.linkedin || target_networks.tiktok || target_networks.x) && state?.data?.step1?.available_ctas && (
                <>
                    <C.Eres_hline />
                    <C.Eres_hline />

                    <C.Eres_form_label htmlFor="eres-cta-section">Call-to-Action Buttons</C.Eres_form_label>
                    <div className='tw-flex tw-flex-col tw-space-y-3'>
                        <p className="tw-text-sm tw-text-gray-600">
                            Select the action button that will appear on your ads for each network
                        </p>

                        {/* facebook cta */}
                        { target_networks.meta && state.data.step1.available_ctas.facebook.length > 0 && (
                            <div>
                                <C.Eres_form_label htmlFor="meta-cta">Facebook CTA</C.Eres_form_label>
                                <select
                                    id="meta-cta"
                                    className={`tw-w-full tw-transition tw-h-9 tw-m-0 tw-text-black tw-font-normal tw-text-base ${C.form_element_styles}`}
                                    value={state.data.step1.meta_cta || ""}
                                    onChange={(e) => dispatch({type: 'SET_STEP1_DATA', payload: {meta_cta: e.target.value || undefined}})}
                                >
                                    <option value="">Select a call-to-action (optional)</option>
                                    {state.data.step1.available_ctas.facebook.map((cta : any) => (
                                        <option key={cta.value} value={cta.value}>{cta.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* linkedin cta */}
                        { target_networks.linkedin && state.data.step1.available_ctas.linkedin.length > 0 && (
                            <div>
                                <C.Eres_form_label htmlFor="linkedin-cta">LinkedIn CTA</C.Eres_form_label>
                                <select
                                    id="linkedin-cta"
                                    className={`tw-w-full tw-transition tw-h-9 tw-m-0 tw-text-black tw-font-normal tw-text-base ${C.form_element_styles}`}
                                    value={state.data.step1.linkedin_cta || ""}
                                    onChange={(e) => dispatch({type: 'SET_STEP1_DATA', payload: {linkedin_cta: e.target.value || undefined}})}
                                >
                                    <option value="">Select a call-to-action (optional)</option>
                                    {state.data.step1.available_ctas.linkedin.map((cta : any) => (
                                        <option key={cta.value} value={cta.value}>{cta.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* tiktok cta */}
                        { target_networks.tiktok && state.data.step1.available_ctas.tiktok.length > 0 && (
                            <div>
                                <C.Eres_form_label htmlFor="tiktok-cta">TikTok CTA</C.Eres_form_label>
                                <select
                                    id="tiktok-cta"
                                    className={`tw-w-full tw-transition tw-h-9 tw-m-0 tw-text-black tw-font-normal tw-text-base ${C.form_element_styles}`}
                                    value={state.data.step1.tiktok_cta || ""}
                                    onChange={(e) => dispatch({type: 'SET_STEP1_DATA', payload: {tiktok_cta: e.target.value || undefined}})}
                                >
                                    <option value="">Select a call-to-action (optional)</option>
                                    {state.data.step1.available_ctas.tiktok.map((cta : any) => (
                                        <option key={cta.value} value={cta.value}>{cta.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* x cta */}
                        { target_networks.x && state.data.step1.available_ctas.x && state.data.step1.available_ctas.x.length > 0 && (
                            <div>
                                <C.Eres_form_label htmlFor="x-cta">X CTA</C.Eres_form_label>
                                <select
                                    id="x-cta"
                                    className={`tw-w-full tw-transition tw-h-9 tw-m-0 tw-text-black tw-font-normal tw-text-base ${C.form_element_styles}`}
                                    value={state.data.step1.x_cta || ""}
                                    onChange={(e) => dispatch({type: 'SET_STEP1_DATA', payload: {x_cta: e.target.value || undefined}})}
                                >
                                    <option value="">Select a call-to-action (optional)</option>
                                    {state.data.step1.available_ctas.x.map((cta : any) => (
                                        <option key={cta.value} value={cta.value}>{cta.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </>
            )}

            { target_networks.meta && 
                <>
                    <C.Eres_hline />
                    <C.Eres_hline />

                    <C.Eres_form_label htmlFor="eres-fb-budget">Meta specific config</C.Eres_form_label>
                    <div>
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-start sm:tw-items-center">
                            <C.Eres_form_label htmlFor="eres-meta-budget">Budget</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-meta-budget" aria-label="name" defaultValue="" type="text" extra={register("meta_budget")} />
                            <C.Eres_form_label htmlFor="eres-meta-bid">Bid amount</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-meta-bid" aria-label="url" defaultValue="" type="text" extra={register("meta_bid_amount")} />
                            <C.Eres_form_label htmlFor="eres-meta-budget-level">Budget applies to</C.Eres_form_label>
                            <select
                                id="eres-meta-budget-level"
                                className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-p-2"
                                {...register("meta_budget_level")}
                            >
                                <option value="ADSET">Each ad set separately</option>
                                <option value="CAMPAIGN">The whole campaign (Meta shares it out)</option>
                            </select>

                            <C.Eres_form_label htmlFor="eres-meta-bid-strategy">Bid strategy</C.Eres_form_label>
                            <select
                                id="eres-meta-bid-strategy"
                                className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-p-2"
                                {...register("meta_bid_strategy")}
                            >
                                <option value="">Let Meta choose</option>
                                <option value="LOWEST_COST_WITHOUT_CAP">Lowest cost</option>
                                <option value="LOWEST_COST_WITH_BID_CAP">Lowest cost, with a bid cap</option>
                                <option value="COST_CAP">Cost cap</option>
                                <option value="LOWEST_COST_WITH_MIN_ROAS">Minimum return on ad spend</option>
                            </select>

                            <C.Eres_form_label htmlFor="eres-meta-pixel">Meta Pixel</C.Eres_form_label>
                            <select
                                id="eres-meta-pixel"
                                className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-p-2"
                                {...register("meta_pixel_id")}
                            >
                                <option value="">
                                    {selected_brand?.meta_pixel_id
                                        ? "Use the brand's pixel"
                                        : "No pixel"}
                                </option>
                                {(meta_pixels ?? []).map(px => (
                                    <option key={px.id} value={String(px.id)}>{px.name}</option>
                                ))}
                            </select>

                            <C.Eres_form_label htmlFor="eres-meta-advantage">Advantage+ audience</C.Eres_form_label>
                            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                                <input
                                    id="eres-meta-advantage"
                                    type="checkbox"
                                    className="tw-w-4 tw-h-4 tw-cursor-pointer"
                                    {...register("meta_advantage_audience")}
                                />
                                Let Meta reach people beyond this audience when it expects better results
                            </label>

                            <C.Eres_form_label htmlFor="eres-brand">Meta Placements</C.Eres_form_label>
                            <Placement_selection_with_preview
                                placements_meta={facebook_placement_with_images}
                                default_values={["facebook_positions__feed", "instagram_positions__stream", "instagram_positions__explore", "instagram_positions__explore_home"]}
                                on_change={handle_facebook_placements_change}
                            />
                        </div>
                    </div>
                </>
            }

            { target_networks.linkedin && 
                <>
                    <C.Eres_hline />
                    <C.Eres_hline />

                    <C.Eres_form_label htmlFor="eres-li-budget">Linkedin specific config</C.Eres_form_label>
                    <div>
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-start sm:tw-items-center">
                            <C.Eres_form_label htmlFor="eres-linkedin-budget">Budget</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-linkedin-budget" aria-label="name" defaultValue="" type="text" extra={register("linkedin_budget")} />
                            <C.Eres_form_label htmlFor="eres-linkedin-bid">Bid amount</C.Eres_form_label>
                            <div className='tw-w-full tw-flex tw-flex-col'>
                            <C.Eres_line_edit id="eres-linkedin-bid" aria-label="url" defaultValue="" type="text" extra={register("linkedin_bid_amount")} />
                                { (errors.linkedin_bid_amount && errors.linkedin_bid_amount.message) && <C.Eres_form_error className="tw-mt-0" msg={errors.linkedin_bid_amount.message} /> }
                            </div>
                            <C.Eres_form_label htmlFor="eres-brand">Placements</C.Eres_form_label>
                            <Placement_selection_with_preview
                                placements_meta={linkedin_placement_with_images}
                                default_values={state?.data?.step1?.linkedin_placements || ["FEED"]}
                                on_change={handle_linkedin_placements_change}
                                extra={linkedin_min_bid} />
                        </div>
                    </div>
                </>
            }

            { target_networks.tiktok &&
                <>
                    <C.Eres_hline />
                    <C.Eres_hline />
                    <C.Eres_form_label htmlFor="eres-tt-budget">Tiktok specific config</C.Eres_form_label>
                    <div>
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-start sm:tw-items-center">
                            <C.Eres_form_label htmlFor="eres-tiktok-budget">Budget</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-tiktok-budget" aria-label="name" defaultValue="" type="text" extra={register("tiktok_budget")} />
                            <C.Eres_form_label htmlFor="eres-tiktok-bid-amount">Bid amount</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-tiktok-bid-amount" aria-label="url" defaultValue="" type="text" extra={register("tiktok_bid_amount")} />
                            <C.Eres_form_label htmlFor="eres-brand">TikTok Placements</C.Eres_form_label>
                            <Placement_selection_with_preview
                                placements_meta={tiktok_placement_with_images}
                                default_values={["TIKTOK_IN_FEED"]}
                                on_change={handle_tiktok_placements_change}
                            />
                        </div>
                    </div>
                </>
            }

            { target_networks.x &&
                <>
                    <C.Eres_hline />
                    <C.Eres_hline />
                    <C.Eres_form_label htmlFor="eres-x-budget">X specific config</C.Eres_form_label>
                    <div>
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-start sm:tw-items-center">
                            <C.Eres_form_label htmlFor="eres-x-budget">Budget</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-x-budget" aria-label="budget" defaultValue="" type="text" extra={register("x_budget")} />
                            <C.Eres_form_label htmlFor="eres-x-bid-amount">Bid amount</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-x-bid-amount" aria-label="bid" defaultValue="" type="text" extra={register("x_bid_amount")} />
                            <C.Eres_form_label htmlFor="eres-x-placements">X Placements</C.Eres_form_label>
                            <Placement_selection_with_preview
                                placements_meta={x_placement_with_images}
                                default_values={["publisher_network"]}
                                on_change={handle_x_placements_change}
                            />
                        </div>
                    </div>
                </>
            }
        </div>
        </form>
    )
}


const Step2_form = () => {
    const [ps, cs] = P.use_authenticated_program_state()
    const [state, dispatch] = useMultistepCreateCampaignContext()
    const [brands] = P.use_brands()
    const [facebook_page_id, set_facebook_page_id] = React.useState<string | undefined>(undefined)
    const [facebook_ad_account_id, set_facebook_ad_account_id] = React.useState<string | undefined>(undefined)
    const [instagram_actor_id, set_instagram_actor_id] = React.useState<string | undefined>(undefined)
    const [linkedin_organization_id, set_linkedin_organization_id] = React.useState<string | undefined>(undefined)
    const [linkedin_ad_account_id, set_linkedin_ad_account_id] = React.useState<string | undefined>(undefined)
    const [x_ad_account_id, set_x_ad_account_id] = React.useState<string | undefined>(undefined)

    // ensure connected networks and brands are loaded
    React.useEffect(() => {
        if (cs.connected_networks === undefined) {
            console.log("Polling connected networks...")
            P.poll_connected_networks(ps, cs)
        }
        if (cs.brands === undefined) {
            console.log("Polling brands...")
            P.poll_connected_brands(ps, cs)
        }
    }, [ps, cs])

    const { register, watch, control, formState: { errors } } = useForm<Creative_detail_step_2_form>(
        {
            defaultValues: {
                ...state?.data?.step2
            }
        }
    )

    const selected_networks = state?.data?.step1?.networks ?
        Object.keys(state?.data?.step1?.networks).filter(key => state.data.step1.networks[key] === true) : [];

    // get the selected brand details
    const selected_brand = brands?.find(b => String(b.id) === String(state?.data?.step1?.brand_id));

    // fetch facebook page id and ad account id when brand and meta network are selected
    React.useEffect(() => {
        const fetch_facebook_assets = async () => {
            if (!selected_networks.includes('meta')) {
                set_facebook_page_id(undefined)
                set_facebook_ad_account_id(undefined)
                set_instagram_actor_id(undefined)
                return
            }

            // fetch facebook page id
            if (selected_brand?.meta_page) {
                try {
                    const [ok, resp] = await P.issue_json_get(
                        P.v1api`social/facebook/pages/${selected_brand.meta_page}`,
                        z.any(),
                        cs.token
                    )

                    if (ok && resp.facebook_id) {
                        set_facebook_page_id(resp.facebook_id)
                    } else {
                        console.warn("Could not fetch Facebook page ID for meta_page:", selected_brand.meta_page)
                    }
                } catch (err) {
                    console.error("Error fetching Facebook page:", err)
                }
            }

            // fetch facebook ad account id
            if (selected_brand?.ad_account_id) {

                if (!cs.connected_networks) {
                    console.warn("Connected networks not loaded yet")
                    return
                }

                try {
                    // find the facebook network connection
                    const facebook_network = cs.connected_networks.find(
                        (network: any) => network.network_tag === 'nw-facebook'
                    )

                    if (!facebook_network) {
                        console.warn("No Facebook network connected")
                        return
                    }

                    // fetch all ad accounts for this facebook user
                    const [ok, ad_accounts] = await P.issue_json_get(
                        P.v1api`social/facebook/${facebook_network.eres_dbid}/ad-accounts`,
                        z.array(z.object({
                            id: z.number(),
                            facebook_id: z.string(),
                            name: z.string()
                        })),
                        cs.token
                    )

                    if (ok) {
                        // find the ad account with matching database id
                        const matching_ad_account = ad_accounts.find(
                            (account: any) => account.id === selected_brand.ad_account_id
                        )

                        if (matching_ad_account) {
                            set_facebook_ad_account_id(matching_ad_account.facebook_id)
                        } else {
                            console.warn("Could not find ad account with database ID:", selected_brand.ad_account_id)
                        }
                    } else {
                        console.warn("Could not fetch Facebook ad accounts:", ad_accounts)
                    }
                } catch (err) {
                    console.error("Error fetching Facebook ad account:", err)
                }
            }

            // fetch instagram actor id
            if (selected_brand?.instagram_asset_id && cs.connected_networks) {
                try {
                    // find the facebook network connection (same as ad account fetching)
                    const facebook_network = cs.connected_networks.find(
                        (network: any) => network.network_tag === 'nw-facebook'
                    )

                    if (!facebook_network) {
                        console.warn("No Facebook network connected for instagram fetch")
                        return
                    }

                    // fetch all instagram accounts for this facebook user
                    const [ok, instagram_accounts] = await P.issue_json_get(
                        P.v1api`social/facebook/${facebook_network.eres_dbid}/instagrams`,
                        z.array(z.object({
                            id: z.number(),
                            facebook_id: z.string(),
                            username: z.string().optional()
                        })),
                        cs.token
                    )

                    if (ok) {
                        // find the instagram account with matching database id
                        const matching_instagram = instagram_accounts.find(
                            (account: any) => account.id === selected_brand.instagram_asset_id
                        )

                        if (matching_instagram) {
                            set_instagram_actor_id(matching_instagram.facebook_id)
                        } else {
                            console.warn("Could not find Instagram account with database ID:", selected_brand.instagram_asset_id)
                        }
                    } else {
                        console.warn("Could not fetch Instagram accounts:", instagram_accounts)
                    }
                } catch (err) {
                    console.error("Error fetching Instagram actor:", err)
                }
            }
        }

        fetch_facebook_assets()
    }, [selected_brand?.meta_page, selected_brand?.ad_account_id, selected_brand?.instagram_asset_id, selected_networks, cs.token, cs.connected_networks]);

    // fetch linkedin organization id
    React.useEffect(() => {
        const fetch_linkedin_organization = async () => {
            if (!selected_brand?.linkedin_org || !selected_networks.includes('linkedin') || !cs.connected_networks) {
                set_linkedin_organization_id(undefined)
                return
            }

            try {
                const linkedin_network = cs.connected_networks.find(
                    (network : any) => network.network_tag === 'nw-linkedin'
                )

                if (!linkedin_network) {
                    console.warn("No LinkedIn network connected")
                    return
                }

                // fetch all linkedin organizations for this user
                const [ok, organizations] = await P.issue_json_get(
                    P.v1api`social/linkedin/${linkedin_network.eres_dbid}/organizations`,
                    z.array(z.object({
                        id: z.number(),
                        linkedin_id: z.number(),
                        name: z.string()
                    })),
                    cs.token
                )

                if (ok) {
                    // find the organization with matching database id
                    const matching_org = organizations.find(
                        (org : any) => org.id === selected_brand.linkedin_org
                    )

                    if (matching_org) {
                        set_linkedin_organization_id(String(matching_org.linkedin_id))
                    } else {
                        console.warn("Could not find LinkedIn organization with database ID:", selected_brand.linkedin_org)
                    }
                } else {
                    console.warn("Could not fetch LinkedIn organizations:", organizations)
                }
            } catch (err) {
                console.error("Error fetching LinkedIn organization:", err)
            }
        }

        fetch_linkedin_organization()
    }, [selected_brand?.linkedin_org, selected_networks, cs.token, cs.connected_networks])

    // fetch linkedin ad account id
    React.useEffect(() => {
        const fetch_linkedin_ad_account = async () => {
            if (!selected_brand?.linkedin_ad_account || !selected_networks.includes('linkedin') || !cs.connected_networks) {
                set_linkedin_ad_account_id(undefined)
                return
            }

            try {
                const linkedin_network = cs.connected_networks.find(
                    (network : any) => network.network_tag === 'nw-linkedin'
                )

                if (!linkedin_network) {
                    console.warn("No LinkedIn network connected")
                    return
                }

                // fetch all linkedin ad accounts for this user
                const [ok, ad_accounts] = await P.issue_json_get(
                    P.v1api`social/linkedin/${linkedin_network.eres_dbid}/ad-accounts`,
                    z.array(z.object({
                        id: z.number(),
                        linkedin_id: z.number(),
                        name: z.string()
                    })),
                    cs.token
                )

                if (ok) {
                    // find the ad account with matching database id
                    const matching_ad_account = ad_accounts.find(
                        (account : any) => account.id === selected_brand.linkedin_ad_account
                    )

                    if (matching_ad_account) {
                        set_linkedin_ad_account_id(String(matching_ad_account.linkedin_id))
                    } else {
                        console.warn("Could not find LinkedIn ad account with database ID:", selected_brand.linkedin_ad_account)
                    }
                } else {
                    console.warn("Could not fetch LinkedIn ad accounts:", ad_accounts)
                }
            } catch (err) {
                console.error("Error fetching LinkedIn ad account:", err)
            }
        }

        fetch_linkedin_ad_account()
    }, [selected_brand?.linkedin_ad_account, selected_networks, cs.token, cs.connected_networks])

    // fetch x ad account id
    React.useEffect(() => {
        const fetch_x_ad_account = async () => {
            if (!selected_brand?.x_ad_account_id || !selected_networks.includes('x') || !cs.connected_networks) {
                set_x_ad_account_id(undefined)
                return
            }

            try {
                const x_network = cs.connected_networks.find(
                    (network : any) => network.network_tag === 'nw-x'
                )

                if (!x_network) {
                    console.warn("No X network connected")
                    return
                }

                // fetch all x ad accounts for this user
                const [ok, ad_accounts] = await P.issue_json_get(
                    P.v1api`social/x/${x_network.eres_dbid}/ad-accounts`,
                    z.array(z.object({
                        id: z.number(),
                        x_id: z.string(),
                        name: z.string()
                    })),
                    cs.token
                )

                if (ok) {
                    // find the ad account with matching database id
                    const matching_ad_account = ad_accounts.find(
                        (account : any) => account.id === selected_brand.x_ad_account_id
                    )

                    if (matching_ad_account) {
                        set_x_ad_account_id(matching_ad_account.x_id)
                    } else {
                        console.warn("Could not find X ad account with database ID:", selected_brand.x_ad_account_id)
                    }
                } else {
                    console.warn("Could not fetch X ad accounts:", ad_accounts)
                }
            } catch (err) {
                console.error("Error fetching X ad account:", err)
            }
        }

        fetch_x_ad_account()
    }, [selected_brand?.x_ad_account_id, selected_networks, cs.token, cs.connected_networks])

    watch((data, { name, type }) => {
        // Only log when specific fields change to reduce noise
        if (name) {
        }
        dispatch({ type: 'SET_STEP2_DATA', payload: data })
    })


    const handle_file_change = async (event : React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            const formData = new FormData();
            formData.append('fileb', file);
            const [ok, resp] = await P.issue_formdata_post(P.v1api`media/files/`, formData, z.any(), cs.token)
            if (ok) {
                dispatch({ type: 'SET_FILE_ID', payload: { file_id: resp.id}})

                // upload to facebook if meta network is selected and we have an ad account
                // this uploads the image to facebook's ad account and returns an image_hash
                // which is required for the preview api to work correctly
                // will gracefully fall back to mockup preview if upload fails (test/sandbox pages)

                if (state?.data?.step1?.networks?.meta && facebook_ad_account_id && file.type.startsWith('image/')) {
                    const fbFormData = new FormData();
                    fbFormData.append('file', file);
                    fbFormData.append('ad_account_id', facebook_ad_account_id);

                    const [fbOk, fbResp] = await P.issue_formdata_post(
                        P.v1api`social/facebook/upload-image`,
                        fbFormData,
                        z.object({ hash: z.string() }),
                        cs.token
                    )

                    if (fbOk) {
                        dispatch({ type: 'SET_FACEBOOK_IMAGE_HASH', payload: { facebook_image_hash: fbResp.hash }})
                    } else {
                        console.warn("Facebook image upload failed; falling back to the mock preview")
                        // this is expected for test/sandbox pages - preview will fall back to mockup
                    }
                } else {
                    console.log("Skipping Facebook image upload - conditions not met")
                }

                // only convert to video if tiktok is selected and it's an image file
                if (state?.data?.step1?.networks?.tiktok && file.type.startsWith('image/')) {
                    const [ok2, resp2] = await P.issue_json_post(
                        P.v1api`media/generate-video-from/${resp.id}?aspect_ratio=9:16`,
                        {},
                        z.object({ task_id: z.string() }),
                        cs.token
                    )
                    if (ok2) {
                        console.log("Video conversion started, task_id:", resp2.task_id)
                        dispatch({type: "SET_TASK_ID", payload: {task_id: resp2.task_id}})
                        dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'converting' }})
                        // mqtt will handle the conversion status update
                    } else {
                        console.warn("Cannot request video file convert for TikTok", resp2)
                        dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'failed' }})
                    }
                }
            } else {
                console.warn("Failed to upload file", resp)
            }
        }
    }

    const upload_video_to_tiktok = async (video_file_id : number) => {
        try {
            // console.log("Uploading video to TikTok, file ID:", video_file_id)

            const videoBlob = await fetch(P.v1api`media/files/${video_file_id}`)
                .then(r => r.blob())

            const formData = new FormData()
            formData.append('file', videoBlob, 'video.mp4')
            formData.append('advertiser_id', TIKTOK_SANDBOX_ADVERTISER_ID)

            const [ok, resp] = await P.issue_formdata_post(
                P.v1api`social/tiktok/upload-video`,
                formData,
                z.object({ video_id: z.string() }),
                cs.token
            )

            if (ok) {
                dispatch({
                    type: 'SET_TIKTOK_VIDEO_ID',
                    payload: { tiktok_video_id: resp.video_id }
                })
                dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'done' }})
            } else {
                console.error("Failed to upload video to TikTok:", resp)
                dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'upload_failed' }})
            }
        } catch (err) {
            console.error("Error uploading video to TikTok:", err)
            dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'upload_failed' }})
        }
    }


    const state_to_preview_data = (state : Multistep_campaign_data) : preview_data_t => {
        return {
            headline: state?.data?.step2.headline,
            message: state?.data?.step2.message,
            link: state?.data?.step2.link,
            file_id: state?.data?.step2?.file_id,
            meta_budget: state?.data?.step1?.meta_budget,
            meta_bid_amount: state?.data?.step1?.meta_bid_amount,
            linkedin_budget: state?.data?.step1?.linkedin_budget,
            linkedin_bid_amount: state?.data?.step1?.linkedin_bid_amount,
            tiktok_budget: state?.data?.step1?.tiktok_budget,
            tiktok_bid_amount: state?.data?.step1?.tiktok_bid_amount
        }
    }

    return (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6 tw-items-start">
            {state && state.data.step2.headline && state.data.step2.message && state.data.step2.link && state.data.step2.file_id ? (
                <Enhanced_campaign_preview
                    networks={selected_networks}
                    placements={{
                        ...(state.data.step1.networks?.meta && {
                            meta: state.data.step1.meta_placements || ["facebook_positions__feed", "instagram_positions__stream"]
                        }),
                        ...(state.data.step1.networks?.linkedin && {
                            linkedin: state.data.step1.linkedin_placements || ["FEED"]
                        }),
                        ...(state.data.step1.networks?.tiktok && {
                            tiktok: state.data.step1.tiktok_placements || ["TIKTOK_IN_FEED"]
                        }),
                        ...(state.data.step1.networks?.x && {
                            x: state.data.step1.x_placements || ["publisher_network"]
                        })
                    }}
                    headline={state.data.step2.headline}
                    message={state.data.step2.message}
                    link={state.data.step2.link}
                    image_url={state.data.step2.file_id ? P.v1api`media/files/${state.data.step2.file_id}` : undefined}
                    facebook_image_hash={state.data.step2.facebook_image_hash}
                    tiktok_image_hash={state.data.step2.tiktok_image_hash}
                    facebook_page_id={facebook_page_id}
                    facebook_ad_account_id={facebook_ad_account_id}
                    instagram_actor_id={instagram_actor_id}
                    linkedin_organization_id={linkedin_organization_id}
                    linkedin_ad_account_id={linkedin_ad_account_id}
                    tiktok_advertiser_id={selected_networks.includes('tiktok') ? TIKTOK_SANDBOX_ADVERTISER_ID : undefined}
                    tiktok_video_id={state.data.step2.tiktok_video_id}
                    x_ad_account_id={x_ad_account_id}
                    facebook_cta={state.data.step1.meta_cta}
                    linkedin_cta={state.data.step1.linkedin_cta}
                    tiktok_cta={state.data.step1.tiktok_cta}
                    x_cta={state.data.step1.x_cta}
                >
                    {({ iframe, controls }) => (
                        <>
                            {/* LEFT COLUMN: Form + Conversion Status + Controls (in a single column) */}
                            <div className="tw-space-y-4">
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-start sm:tw-items-center">
                                    <C.Eres_form_label htmlFor="eres-creative-headline" required>Headline</C.Eres_form_label>
                                    <C.Eres_line_edit id="eres-creative-headline" aria-label="headline" type="text" {...register("headline")} />

                                    <C.Eres_form_label htmlFor="eres-audience-message" required>Message</C.Eres_form_label>
                                    <textarea rows={4} className={`tw-transition tw-m-0 tw-text-black tw-font-normal tw-text-base ${C.form_element_styles}`} {...register("message")} />

                                    <C.Eres_form_label htmlFor="eres-audience-link" required>Link</C.Eres_form_label>
                                    <C.Eres_line_edit id="eres-audience-link" aria-label="name" type="text" {...register("link")} />

                                    <C.Eres_form_label htmlFor="eres-audience-media" required>Media</C.Eres_form_label>
                                    <div>
                                        <C.Eres_file_input on_change={handle_file_change} />
                                    </div>
                                </div>

                                {state && state.data.step2.conversion_status === 'converting' && (
                                    <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded tw-p-4">
                                        <div className="tw-text-blue-800 tw-font-medium">
                                            Converting image to video for TikTok...
                                        </div>
                                        <div className="tw-text-blue-600 tw-text-sm tw-mt-1">
                                            This may take 10-15 seconds
                                        </div>
                                    </div>
                                )}
                                {state && state.data.step2.conversion_status === 'uploading' && (
                                    <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded tw-p-4">
                                        <div className="tw-text-blue-800 tw-font-medium">
                                            Uploading video to TikTok...
                                        </div>
                                    </div>
                                )}
                                {state && state.data.step2.conversion_status === 'failed' && (
                                    <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-4">
                                        <div className="tw-text-blue-800 tw-font-medium">
                                            Video conversion failed
                                        </div>
                                        <div className="tw-text-red-600 tw-text-sm tw-mt-1">
                                            Please try uploading a different image
                                        </div>
                                    </div>
                                )}
                                {state && state.data.step2.conversion_status === 'upload_failed' && (
                                    <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-4">
                                        <div className="tw-text-red-800 tw-font-medium">
                                            Failed to upload video to TikTok
                                        </div>
                                        <div className="tw-text-red-600 tw-text-sm tw-mt-1">
                                            Please try again
                                        </div>
                                    </div>
                                )}

                                {controls}
                            </div>

                            {/* RIGHT COLUMN: Preview iframe */}
                            {iframe}
                        </>
                    )}
                </Enhanced_campaign_preview>
            ) : (
                <>
                    {/* LEFT COLUMN: Form + Conversion Status + Controls (in a single column) */}
                    <div className="tw-space-y-4">
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-start sm:tw-items-center">
                            <C.Eres_form_label htmlFor="eres-creative-headline" required>Headline</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-creative-headline" aria-label="headline" type="text" {...register("headline")} />

                            <C.Eres_form_label htmlFor="eres-audience-message" required>Message</C.Eres_form_label>
                            <textarea rows={4} className={`tw-transition tw-m-0 tw-text-black tw-font-normal tw-text-base ${C.form_element_styles}`} {...register("message")} />

                            <C.Eres_form_label htmlFor="eres-audience-link" required>Link</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-audience-link" aria-label="name" type="text" {...register("link")} />

                            <C.Eres_form_label htmlFor="eres-audience-media" required>Media</C.Eres_form_label>
                            <div>
                                <C.Eres_file_input on_change={handle_file_change} />
                            </div>
                        </div>

                        {state && state.data.step2.conversion_status === 'converting' && (
                            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded tw-p-4">
                                <div className="tw-text-blue-800 tw-font-medium">
                                    Converting image to video for TikTok...
                                </div>
                                <div className="tw-text-blue-600 tw-text-sm tw-mt-1">
                                    This may take 10-15 seconds
                                </div>
                            </div>
                        )}
                        {state && state.data.step2.conversion_status === 'uploading' && (
                            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded tw-p-4">
                                <div className="tw-text-blue-800 tw-font-medium">
                                    Uploading video to TikTok...
                                </div>
                            </div>
                        )}
                        {state && state.data.step2.conversion_status === 'failed' && (
                            <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-4">
                                <div className="tw-text-blue-800 tw-font-medium">
                                    Video conversion failed
                                </div>
                                <div className="tw-text-red-600 tw-text-sm tw-mt-1">
                                    Please try uploading a different image
                                </div>
                            </div>
                        )}
                        {state && state.data.step2.conversion_status === 'upload_failed' && (
                            <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-4">
                                <div className="tw-text-red-800 tw-font-medium">
                                    Failed to upload video to TikTok
                                </div>
                                <div className="tw-text-red-600 tw-text-sm tw-mt-1">
                                    Please try again
                                </div>
                            </div>
                        )}

                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-p-6">
                            <h3 className="tw-text-lg tw-font-semibold tw-mb-4">Preview</h3>
                            <div className="tw-text-gray-500 tw-text-sm">
                                Fill in the form to see preview controls
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Preview iframe */}
                    <div className="tw-min-h-[500px] tw-max-h-[800px] tw-flex tw-items-center tw-justify-center tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                        <div className="tw-text-gray-400">Preview will appear here</div>
                    </div>
                </>
            )}
        </div>
    )
}


const Debug_form = () => {
    const [state, dispatch] = useMultistepCreateCampaignContext()

    // show debug in development mode (not in production or fbapp)
    const is_dev_mode = import.meta.env.MODE === 'development' ||
                        import.meta.env.DEV === true ||
                        window.location.hostname === 'localhost'

    if (!is_dev_mode) {
        return null
    }

    return (
        <div>
            <pre>{JSON.stringify(state, null, 2)}</pre>
        </div>
    )
}


export const Create_campaign_form = () => {
    const nav = useNavigate()

    const { connectStatus, mqttSub, payload } = useContext(MqttContext);
    const [ps, cs] = P.use_authenticated_program_state()
    const [state, dispatch] = useMultistepCreateCampaignContext()

    P.run_once_on_component_creation(() => {
        void P.poll_connected_brands(ps, cs)
        void P.poll_saved_audiences(ps, cs)
    })

    useEffect(() => {
    console.log("MQTT Connection Status:", connectStatus)
    if (connectStatus == "Connected") {
        console.log("MQTT Connected - Subscribing to topic: eres/worker")
        mqttSub({ topic: "eres/worker", qos: 0 });
    }
    }, [connectStatus])

    useEffect(() => {
        let { message: mqttMessage } = payload;
        if (typeof mqttMessage === 'undefined') {
          return
        }

        let messageAsObj = JSON.parse(mqttMessage);
        // console.log("Raw MQTT message received:", messageAsObj)
        let { type, message } = messageAsObj;

        if (type !== "media/file-converter") {
          console.log("MQTT message type mismatch. Expected 'media/file-converter', got:", type)
          return
        }

        // console.log("MQTT message type matches 'media/file-converter'")
        // console.log("Current task_id:", state?.data?.step2?.task_id)
        // console.log("Message task_id:", message.task_id)

        if (!state || message.task_id !== state.data.step2.task_id) {
          console.log("MQTT message for different task, ignoring")
          return
        }

        if (message.status === "DONE" && message.result_file_id) {
            console.log("Video conversion complete via MQTT. Result file ID:", message.result_file_id)
            dispatch({type: "SET_VIDEO_ID", payload: { video_id: message.result_file_id}})
            dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'uploading' }})

            // upload to tiktok
            ;(async () => {
                try {
                    // console.log("Starting TikTok video upload. File ID:", message.result_file_id)
                    const videoBlob = await fetch(P.v1api`media/files/${message.result_file_id}`)
                        .then(r => r.blob())

                    // console.log("Video blob fetched. Size:", videoBlob.size, "bytes")

                    const formData = new FormData()
                    formData.append('file', videoBlob, 'video.mp4')
                    formData.append('advertiser_id', TIKTOK_SANDBOX_ADVERTISER_ID)

                    // console.log("Sending video to TikTok upload endpoint...")
                    const [ok, resp] = await P.issue_formdata_post(
                        P.v1api`social/tiktok/upload-video`,
                        formData,
                        z.object({ video_id: z.string() }),
                        cs.token
                    )

                    if (ok) {
                        dispatch({
                            type: 'SET_TIKTOK_VIDEO_ID',
                            payload: { tiktok_video_id: resp.video_id }
                        })
                        dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'done' }})
                        // console.log("Preview should now be triggered with video_id:", resp.video_id)
                    } else {
                        console.error("Failed to upload video to TikTok:", resp)
                        dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'upload_failed' }})
                    }
                } catch (err) {
                    console.error("Error uploading video to TikTok:", err)
                    dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'upload_failed' }})
                }
            })()
        } else if (message.status === "FAILED") {
            console.error("Video conversion FAILED via MQTT")
            console.error("Failure reason:", message.message || "No error message provided")
            dispatch({type: 'SET_CONVERSION_STATUS', payload: { conversion_status: 'failed' }})
        } else {
            console.warn("Unknown message status:", message.status)
        }

        // console.log("MQTT message: ", messageAsObj)

    }, [payload])

    const [current_step_index, set_current_step_index] = React.useState(0);

    const on_step_click = (step_index : number) => {
        set_current_step_index(step_index)
    }

    const steps : C.step_meta[] = [
        {
            title: 'Campaign setting',
            content: <Step1_form />,
        },
        {
            title: 'Creative details',
            content: <Step2_form />,
        }
    ];

    const next = () => {
        set_current_step_index(Math.min(current_step_index + 1, steps.length - 1));
    };
    
    const previous = () => {
        set_current_step_index(Math.max(current_step_index - 1, 0));
    };

    const [submit_error, set_submit_error] = React.useState<string | null>(null)
    const [submitting, set_submitting] = React.useState(false)
    const [all_brands] = P.use_brands()

    /**
     * What has to be true before facebook will accept the campaign. Checked here
     * rather than left to the worker: the api returns 200 and the failure only
     * shows up later in the campaign's status, so the person who made the
     * mistake never sees it.
     */
    const validation_problems = () : string[] => {
        if (state === undefined) return ["Something went wrong. Please reload the page."]
        const s1 = state.data.step1
        const s2 = state.data.step2
        const problems : string[] = []

        if (!s1.name?.trim()) problems.push("Give the campaign a name.")
        if (!s1.brand_id) problems.push("Choose a brand.")
        if (!s1.audience_id) problems.push("Choose an audience.")

        const chosen_networks = Object.keys(s1.networks).filter(k => s1.networks[k])
        if (chosen_networks.length === 0) problems.push("Choose at least one network.")

        if (s1.networks["meta"]) {
            if (!(Number(s1.meta_budget) > 0)) problems.push("Enter a Meta budget greater than zero.")
            if (!(Number(s1.meta_bid_amount) > 0)) problems.push("Enter a Meta bid amount greater than zero.")
            if (!s1.meta_placements?.length) problems.push("Choose at least one Meta placement.")
            // the worker refuses these two objectives without a pixel, and the
            // brand is where the pixel comes from
            const needs_pixel = s1.objective === "CONVERSIONS" || s1.objective === "LEAD_GENERATION"
            const brand = all_brands?.find(b => String(b.id) === String(s1.brand_id))
            // a lead campaign using a native instant form collects the lead on
            // facebook, so it needs no pixel at all
            const has_lead_form = s1.objective === "LEAD_GENERATION" && !!s1.meta_leadgen_form_id
            if (needs_pixel && !has_lead_form && !s1.meta_pixel_id && !brand?.meta_pixel_id) {
                problems.push(
                    "This objective needs a Meta Pixel. Select one below, or add one to the brand.")
            }
        }

        if (s1.budget_mode === "LIFETIME" && !s1.end_time) {
            problems.push("A lifetime budget needs an end date.")
        }

        if (!s2.headline?.trim()) problems.push("Enter a headline.")
        if (!s2.message?.trim()) problems.push("Enter the ad text.")
        if (!s2.link?.trim()) problems.push("Enter a destination link.")
        if (!s2.file_id) problems.push("Upload an image or video.")

        return problems
    }

    const submit_the_form = async () => {
        if (state === undefined) {
            set_submit_error("Something went wrong. Please reload the page.")
            return
        }

        const problems = validation_problems()
        if (problems.length > 0) {
            set_submit_error(problems.join(" "))
            return
        }
        set_submit_error(null)
        set_submitting(true)

        let creation_payload : campaign_creation_t = {
            "smart_campaign": {
                "name": state.data.step1.name,
                "objective": state.data.step1.objective,
                "brand_id": state.data.step1.brand_id,
                "networks": Object.keys(state.data.step1.networks).filter((k) => state.data.step1.networks[k]),
                "budget_mode": state.data.step1.budget_mode,
                "start_time": state.data.step1.start_time,
                "end_time": state.data.step1.end_time,
                "audience_id": state.data.step1.audience_id
            },
            "smart_creative": {
                "name": state.data.step1.name,
                "headline": state.data.step2.headline,
                "message": state.data.step2.message,
                "link": state.data.step2.link,
                "file_id": state.data.step2.file_id
            },
            "specific_config__facebook": {
                "budget": state.data.step1.meta_budget,
                "bid_amount": state.data.step1.meta_bid_amount,
                "placements": state.data.step1.meta_placements,
                ...(state.data.step1.meta_cta && { "call_to_action": state.data.step1.meta_cta }),
                ...(state.data.step1.meta_pixel_id && { "pixel_id": state.data.step1.meta_pixel_id }),
                ...(state.data.step1.meta_budget_level && { "budget_level": state.data.step1.meta_budget_level }),
                ...(state.data.step1.meta_bid_strategy && { "bid_strategy": state.data.step1.meta_bid_strategy }),
                ...(state.data.step1.meta_advantage_audience !== undefined && {
                    "advantage_audience": state.data.step1.meta_advantage_audience }),
                ...(state.data.step1.meta_leadgen_form_id && {
                    "leadgen_form_id": state.data.step1.meta_leadgen_form_id })
            },
            "specific_config__linkedin": {
                "budget": state.data.step1.linkedin_budget,
                "bid_amount": state.data.step1.linkedin_bid_amount,
                "placements": state.data.step1.linkedin_placements,
                ...(state.data.step1.linkedin_cta && { "call_to_action": state.data.step1.linkedin_cta })
                // @TODO: toggle conversion_tracking_id
                // "conversion_tracking_id": state.data.step1.linkedin_conversion_tracking_id
            },
            "specific_config__tiktok": {
                "budget": state.data.step1.tiktok_budget,
                "bid_amount": state.data.step1.tiktok_bid_amount,
                "placements": state.data.step1.tiktok_placements,
                ...(state.data.step1.tiktok_cta && { "call_to_action": state.data.step1.tiktok_cta })
            }
        }

        const [ok, resp] = await P.issue_json_post(P.v1api`smart-campaign/campaigns/`, creation_payload, z.any(), cs.token)
        set_submitting(false)

        if (ok) {
            nav("/campaigns")
        } else {
            // this used to be a console.log, so a failed publish looked
            // identical to nothing happening
            const detail =
                (resp as {detail?: unknown})?.detail ??
                (resp as {message?: unknown})?.message
            set_submit_error(
                typeof detail === "string" && detail.length > 0
                    ? detail
                    : "We could not publish this campaign. Please try again.")
        }
    }

    const in_wizard_mode = state?.in_wizard_mode ?? false

    return (
        <>
            <C.Eres_bare_card className="tw-mb-6">
                <div>
                    <C.Eres_header2xl className="tw-font-semibold">Create a new campaign</C.Eres_header2xl>
                    <p>A smart campaign can be created once and publish across multiple networks, allowing for easily create and manage campaign</p>
                </div>
            </C.Eres_bare_card>

            {in_wizard_mode ? (
                <>
                    <C.Eres_card>
                        <div className="tw-p-3">
                            {steps[current_step_index].content}
                        </div>
                    </C.Eres_card>

                    {submit_error !== null && (
                        <div role="alert" className="tw-mb-3 tw-rounded tw-border tw-border-red-300 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-800">
                            {submit_error}
                        </div>
                    )}
                    {/* Campaign form navigation - shown in wizard mode */}
                    <div className="tw-py-3">
                        <div className="tw-flex tw-justify-end tw-space-x-2">
                        { current_step_index > 0 && (
                            <C.Eres_iconbutton icon={faCircleChevronLeft} onClick={previous}>Previous step</C.Eres_iconbutton>
                        ) }

                        { current_step_index < steps.length - 1 && (
                            <C.Eres_iconbutton icon={faCircleChevronRight} onClick={next}>Next step</C.Eres_iconbutton>
                        ) }

                        { current_step_index == steps.length - 1 && (
                            <C.Eres_iconbutton icon={faFloppyDisk} onClick={submit_the_form}>{submitting ? "Publishing…" : "Submit campaign"}</C.Eres_iconbutton>
                        ) }
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <C.Eres_stepper steps={steps} active={current_step_index} on_step_click={on_step_click} />

                    {submit_error !== null && (
                        <div role="alert" className="tw-mb-3 tw-rounded tw-border tw-border-red-300 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-800">
                            {submit_error}
                        </div>
                    )}

                    <div className="tw-py-3">
                        <div className="tw-flex tw-justify-end tw-space-x-2">
                        { current_step_index > 0 && (
                            <C.Eres_iconbutton icon={faCircleChevronLeft} onClick={previous}>Previous</C.Eres_iconbutton>
                        ) }

                        { current_step_index < steps.length - 1 && (
                            <C.Eres_iconbutton icon={faCircleChevronRight} onClick={next}>Next</C.Eres_iconbutton>
                        ) }

                        { current_step_index == steps.length - 1 && (
                            <C.Eres_iconbutton icon={faFloppyDisk} onClick={submit_the_form}>{submitting ? "Publishing…" : "Submit"}</C.Eres_iconbutton>
                        ) }
                        </div>
                    </div>
                </>
            )}
        </>
    )
}


const Create_campaign: React.FC = () => {
    return (
        <Emit_main page_id="PAGE-campaigns">
            <MqttInitializer>
                <MultistepCreateCampaignProvider>
                    <Debug_form />
                    <Create_campaign_form />
                </MultistepCreateCampaignProvider>
            </MqttInitializer>
        </Emit_main>
    )
};

export default Create_campaign;
